import {declinationOfNumber} from 'src/utils/declination'

// Лимит точки видит тот, у кого на ней право просмотра лимитов, или тот, кто
// правит лимиты точек её компании (как на бэкенде, get_limit_visible_endpoint_ids).
export const canViewEndpointLimit = (endpointPermissions) => Boolean(
  endpointPermissions?.view_endpoint_limits
  || endpointPermissions?.merchant_permissions?.can_edit_endpoint_limits
)

// Доступ к разделу «Точки» ради лимитов: право просмотра лимита хотя бы на одной
// точке или право правки лимитов у мерчанта. Не только по выбранной в
// переключателе точке: право на лимиты относится к конкретным точкам. Но только
// в выбранной компании — список точек показывает её, иначе раздел откроется пустым.
export const hasEndpointLimitsAccess = (permissions, merchantId) => (
  Boolean(merchantId) && Object.values(permissions?.endpoints || {}).some((endpointPermissions) => (
    endpointPermissions?.merchant_id === merchantId && canViewEndpointLimit(endpointPermissions)
  ))
)

// /endpoint/ и /endpoint/{id}/ отдают баланс без проверки view_balance. Управляющему
// точками раздел показывает его как раньше, пришедшему ради лимитов — только с правом.
export const canSeeEndpointBalance = (canManageEndpoints, endpointPermissions) => (
  Boolean(canManageEndpoints || endpointPermissions?.view_balance)
)

// Сумма лимита: деньги приходят строкой с двумя знаками, копейки — только ненулевые.
export const formatLimitAmount = (value) => Number(value).toLocaleString('ru', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).replace(',', '.')

// Порог null — без ограничения по нему.
const isThresholdSet = (value) => value !== null && value !== undefined

// Остаток по каждому суточному порогу; null — порог не задан. Остаток считает бэкенд.
const remainingByThreshold = (limit) => ({
  amount: isThresholdSet(limit.day_amount_limit)
    ? `${formatLimitAmount(limit.remaining_amount)} ₽`
    : null,
  count: isThresholdSet(limit.day_count_limit)
    ? declinationOfNumber(limit.remaining_count, ['операция', 'операции', 'операций'])
    : null,
})

// Остаток суточного лимита за сегодня по заданным порогам: «2 000 ₽», «3 операции».
// Пусто — лимита нет или у него нет суточных порогов.
export const limitRemainingParts = (limit) => {
  if (!limit?.limit_type) {
    return []
  }
  const {amount, count} = remainingByThreshold(limit)
  return [amount, count].filter(Boolean)
}

// Один остаток для виджета в «Операциях»: сумма или операции — по той же настройке,
// что у лимита пользователя (limitTypeView), а если такого порога нет — другой.
export const limitRemainingValue = (limit, typeView = 'amount') => {
  if (!limit?.limit_type) {
    return null
  }
  const {amount, count} = remainingByThreshold(limit)
  return typeView === 'count' ? (count ?? amount) : (amount ?? count)
}

// Использовано за сегодня — для подсказки в форме лимита: сколько уже прошло и
// насколько поднимать порог. Учёт ведётся и без порога, null — данных нет.
export const limitUsedToday = (limit) => ({
  count: isThresholdSet(limit?.used_count)
    ? declinationOfNumber(Number(limit.used_count), ['операция', 'операции', 'операций'])
    : null,
  amount: isThresholdSet(limit?.used_amount)
    ? `${formatLimitAmount(limit.used_amount)} ₽`
    : null,
})

export const isLimitExhausted = (limit) => Boolean(limit?.limit_type) && (
  (isThresholdSet(limit.day_amount_limit) && Number(limit.remaining_amount) <= 0)
  || (isThresholdSet(limit.day_count_limit) && Number(limit.remaining_count) <= 0)
)
